import React, { useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useAlerts } from 'src/common/AlertManager';
import { useIssueCredentialMutation } from 'src/api/mutations/schedulesMutations';
import { loadPkceSession, clearPkceSession } from 'src/utils/pkce';
import styles from './AuthorizeBounce.module.scss';

// The credential exchange refuses a code issued to a different account than
// the one signed in to the dashboard (403). That happens when the browser was
// signed in to FileSpin as someone else at the consent step; say so, rather
// than the raw "authorization code does not match authenticated user".
export const exchangeErrorMessage = (err: any): string => {
  const message: string = err?.message || '';
  if (/does not match authenticated user/i.test(message)) {
    return 'That authorization was for a different FileSpin account than the one you are signed in '
      + 'with here. Try again, and on the authorization page choose "Use a different account" '
      + 'to sign in as yourself.';
  }
  return message || 'Failed to exchange authorization code';
};

export const OAuthCallback: React.FC = () => {
  const history = useHistory();
  const location = useLocation();
  const { showSuccess } = useAlerts();
  const [error, setError] = useState<string | null>(null);

  const returnToRef = React.useRef('/ai/schedules');

  const issueMutation = useIssueCredentialMutation({
    onSuccess: () => {
      showSuccess('Scheduler authorized successfully');
      clearPkceSession();
      history.replace(returnToRef.current);
    },
    onError: (err: any) => {
      setError(exchangeErrorMessage(err));
    },
  });

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const code = params.get('code');
    const state = params.get('state');
    const errorParam = params.get('error');

    if (errorParam) {
      setError(`Authorization denied: ${errorParam}`);
      return;
    }

    if (!code || !state) {
      setError('Missing authorization code or state parameter.');
      return;
    }

    const session = loadPkceSession();
    if (!session || session.state !== state) {
      setError('Invalid state parameter. The authorization session may have expired. Please try again.');
      return;
    }

    returnToRef.current = session.returnTo || '/ai/schedules';
    const redirectUri = `${window.location.origin}/ai/schedules/oauth-callback`;
    issueMutation.mutate({
      code,
      redirect_uri: redirectUri,
      code_verifier: session.verifier,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (error) {
    return (
      <div className={styles.page}>
        <div className={styles.card}>
          <h2>Authorization failed</h2>
          <p>{error}</p>
          <button
            className={styles.btn}
            onClick={() => {
              clearPkceSession();
              history.push('/ai/schedules/authorize');
            }}
          >
            Try again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <div className={styles.card}>
        <div className={styles.spinner} />
        <p>Completing authorization…</p>
      </div>
    </div>
  );
};
